/**
 * App uninstall confirmation dialog.
 * Requires the user to type the app name before uninstalling.
 */

import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Trash2 } from "lucide-react"

interface AppUninstallDialogProps {
  appName: string
  open: boolean
  isUninstalling: boolean
  onConfirm: () => Promise<void> | void
  onOpenChange: (open: boolean) => void
}

export function AppUninstallDialog({
  appName,
  open,
  isUninstalling,
  onConfirm,
  onOpenChange,
}: AppUninstallDialogProps) {
  const [confirmName, setConfirmName] = useState("")
  const uninstallNameMatches = confirmName.trim() === appName

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setConfirmName("")
    }

    onOpenChange(nextOpen)
  }

  const handleConfirm = async () => {
    await onConfirm()
    setConfirmName("")
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Uninstall App</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to uninstall "{appName}"? This action cannot be undone and may affect linked app resources.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="confirm-app-name">Type the app name to confirm</Label>
          <Input
            id="confirm-app-name"
            value={confirmName}
            onChange={(event) => setConfirmName(event.target.value)}
            placeholder={appName}
            disabled={isUninstalling}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isUninstalling}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!uninstallNameMatches || isUninstalling}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isUninstalling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Uninstall App
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

AppUninstallDialog.displayName = "AppUninstallDialog"
